import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Inbox, AlertCircle, Mail, ArrowLeft } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const errorMsg = searchParams.get("error");

  // Email OTP flow state
  const [step, setStep] = useState("choice"); // "choice" | "email" | "code"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    checkAuthAndSession();
  }, []);

  const checkAuthAndSession = async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (res.ok) {
        navigate("/", { replace: true });
        return;
      }
    } catch {
      // not logged in — show login form
    }
    setIsLoading(false);
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/profile`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Indtast en gyldig email");
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API}/auth/email/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!r.ok) throw new Error();
      setStep("code");
      toast.success("Hvis din email er godkendt, er en kode sendt");
    } catch {
      toast.error("Kunne ikke sende kode. Prøv igen.");
    } finally {
      setSending(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) {
      toast.error("Koden skal være 6 cifre");
      return;
    }
    setVerifying(true);
    try {
      const r = await fetch(`${API}/auth/email/verify`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: c }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || "Verificering fejlede");
      }
      toast.success("Logget ind");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.message || "Ugyldig kode");
    } finally {
      setVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
            <Inbox className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Inbound Hub</h1>
          <p className="text-muted-foreground mt-2">Owntune shared ingestion</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {step === "code" ? "Indtast din kode" : "Log ind"}
            </CardTitle>
            <CardDescription>
              {step === "code"
                ? `Vi har sendt en 6-cifret kode til ${email}. Den er gyldig i 10 minutter.`
                : "Kun godkendte brugere kan logge ind. Ingen selvregistrering."}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {errorMsg && (
              <Alert variant="destructive" className="mb-4" data-testid="login-error-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errorMsg}</AlertDescription>
              </Alert>
            )}

            {step === "choice" && (
              <div className="space-y-3">
                <Button
                  onClick={() => setStep("email")}
                  className="w-full h-12 text-base"
                  data-testid="email-login-btn"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Log ind med email
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">backup</span>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  onClick={handleGoogleLogin}
                  className="w-full h-11 text-sm"
                  data-testid="google-login-btn"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </div>
            )}

            {step === "email" && (
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <div>
                  <label htmlFor="email" className="text-sm font-medium">Email</label>
                  <Input
                    id="email"
                    type="email"
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="navn@firma.dk"
                    disabled={sending}
                    className="mt-1"
                    data-testid="email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={sending || !email.trim()}
                  className="w-full h-11"
                  data-testid="send-code-btn"
                >
                  {sending ? "Sender..." : "Send kode"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep("choice")}
                  className="w-full text-sm"
                  disabled={sending}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Tilbage
                </Button>
              </form>
            )}

            {step === "code" && (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div>
                  <label htmlFor="code" className="text-sm font-medium">6-cifret kode</label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    disabled={verifying}
                    className="mt-1 text-center text-2xl tracking-[0.5em] font-mono"
                    data-testid="code-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={verifying || code.length !== 6}
                  className="w-full h-11"
                  data-testid="verify-code-btn"
                >
                  {verifying ? "Verificerer..." : "Log ind"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setStep("email"); setCode(""); }}
                  className="w-full text-sm"
                  disabled={verifying}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Skift email
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
